import { SearchOutlined } from "@ant-design/icons";
import { Input } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { graphql } from "react-relay";
import { useLazyLoadQuery, usePaginationFragment } from "react-relay/hooks";

import { useConnectionNodes } from "components/common/Pagination";
import { useSelector } from "hooks/redux";
import { getOrgUuid } from "state/current_user/selectors";

import MultiSelectFilter from "./MultiSelectFilter";

import type { FC } from "react";
import type {
  ResourceGroupFilter_organization$data,
  ResourceGroupFilter_organization$key,
} from "__generated__/ResourceGroupFilter_organization.graphql";
import type { ResourceGroupFilterQuery } from "__generated__/ResourceGroupFilterQuery.graphql";
import type { ConnectionNodeType } from "components/common/Pagination";
import type { BaseProps } from "./SelectFilter";

export const rgFragment = graphql`
  fragment ResourceGroupFilter_organization on Organization
  @argumentDefinitions(cursor: { type: "String" }, count: { type: "Int", defaultValue: 10 })
  @refetchable(queryName: "ResourceGroupFilterRefetchQuery") {
    id
    resourceGroups(first: $count, after: $cursor)
      @connection(key: "ResourceGroupFilter_resourceGroups") {
      edges {
        node {
          id
          uuid
          name
          isOrganizationDefault
        }
      }
    }
  }
`;

export const query = graphql`
  query ResourceGroupFilterQuery($orgUuid: String!, $cursor: String, $count: Int!) {
    organization(uuid: $orgUuid) {
      ...ResourceGroupFilter_organization @arguments(cursor: $cursor, count: $count)
    }
  }
`;

export type ResourceGroup = ConnectionNodeType<
  ResourceGroupFilter_organization$data["resourceGroups"]
>;

export const getRgTitle = (resourceGroup: ResourceGroup) => {
  if (resourceGroup?.isOrganizationDefault) {
    return "Entire Organization";
  }
  return resourceGroup?.name ?? "";
};

interface Props extends BaseProps {
  onSelect?: (resourceGroupIds: string[]) => void;
  convertParamVariable?: (resourceGroupIds: string) => string[];
  disabled?: boolean;
}

const PAGE_SIZE = 100;

const ResourceGroupFilter: FC<Props> = ({
  onSelect,
  className,
  style,
  placeholder,
  paramKey,
  convertParamVariable,
  disabled = false,
}) => {
  const orgUuid = useSelector(getOrgUuid);
  const data = useLazyLoadQuery<ResourceGroupFilterQuery>(query, {
    orgUuid,
    count: PAGE_SIZE,
  });

  const [groupSearch, setSearch] = useState("");
  const onSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  }, []);

  const maxTags = 0; // Don't show selection, just show # selected

  const setValue = useMemo(() => (disabled ? [] : undefined), [disabled]);

  const spaceStyle = useMemo(() => ({ padding: "8px" }), []);
  const onKeyDown = useCallback((e: React.KeyboardEvent) => e.stopPropagation(), []);
  const searchIcon = useMemo(() => <SearchOutlined />, []);

  const dropdownRender = useCallback(
    (menu: React.ReactElement<unknown>) => (
      <div className="flex flex-col gap-2" style={spaceStyle}>
        <Input
          placeholder="Search"
          prefix={searchIcon}
          onChange={onSearchChange}
          onKeyDown={onKeyDown}
        />
        {menu}
      </div>
    ),
    [onSearchChange, onKeyDown, spaceStyle, searchIcon]
  );

  const paginationQuery = usePaginationFragment<
    ResourceGroupFilterQuery,
    ResourceGroupFilter_organization$key
  >(rgFragment, data.organization);

  useEffect(() => {
    if (paginationQuery.hasNext) {
      paginationQuery.loadNext(PAGE_SIZE);
    }
  }, [paginationQuery]);

  const connectionNodes = useConnectionNodes(paginationQuery?.data?.resourceGroups);

  const getKey = useCallback((resourceGroup: ResourceGroup) => resourceGroup?.id!, []);
  const getValue = useCallback((resourceGroup: ResourceGroup) => resourceGroup?.uuid!, []);

  const spanStyle = useMemo(() => ({ color: "#0077EE" }), []);
  const maxTagPlaceholder = useCallback(
    (omittedValues: unknown[]) => (
      <span style={spanStyle}>{"Groups (" + omittedValues.length + ")"}</span>
    ),
    [spanStyle]
  );

  return (
    <MultiSelectFilter<ResourceGroup, string>
      setValue={setValue}
      onSelect={onSelect}
      className={className}
      style={style}
      placeholder={placeholder || "Search Groups"}
      data={connectionNodes}
      getKey={getKey}
      getValue={getValue}
      getTitle={getRgTitle}
      paramKey={paramKey}
      convertParamVariable={convertParamVariable}
      dropdownRender={dropdownRender}
      maxTagCount={maxTags}
      maxTagPlaceholder={maxTagPlaceholder}
      disabled={disabled}
      showSearch={false}
      searchValue={groupSearch}
    />
  );
};

export default ResourceGroupFilter;
